import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import type { IdentifyTraits } from "@rudderstack/analytics-js";
import { RudderAnalytics } from "@rudderstack/analytics-js";
import { isPatientPortalPath } from "./isPatientPortalPath";

// Customer.io is the one pipe the portal stays off: it is outbound messaging,
// so identifying a patient there makes them addressable in campaigns whatever
// their recorded marketing consent says.
const IS_PATIENT_PORTAL = isPatientPortalPath(window.location.pathname);

// Write key is injected per-environment at build time and substituted into the
// bundle by Vite's `define` (see vite.config.ts). It's read off `process.env`
// rather than `import.meta.env` so this module — imported widely across the app
// — stays parseable by Jest's CJS transform, which can't handle `import.meta`.
const RUDDERSTACK_WRITE_KEY =
  process.env.VITE_RUDDERSTACK_WRITE_KEY || undefined;

// Initialize Rudderstack; init is skipped when the write key is absent (e.g.
// Playwright, or a build without the key configured) so analytics no-ops
// cleanly rather than throwing.
let rudderAnalytics: RudderAnalytics | undefined;
if (window.Playwright) {
  console.log("Playwright detected, skipping Rudderstack initialization");
} else if (!RUDDERSTACK_WRITE_KEY) {
  console.log(
    "VITE_RUDDERSTACK_WRITE_KEY not set, skipping Rudderstack initialization",
  );
} else {
  rudderAnalytics = new RudderAnalytics();
  rudderAnalytics.load(
    RUDDERSTACK_WRITE_KEY,
    "https://versemedicfaxl.dataplane.rudderstack.com",
  );
  rudderAnalytics.ready(() => {
    console.log("Rudderstack is ready");
  });
}

// Initialize the Customer.io CDP browser SDK alongside RudderStack — two
// parallel pipes (we do NOT route one through the other). Same per-env key
// pattern as above (read off process.env to stay Jest-parseable). The In-App
// Plugin renders c.io in-app messages; the In-App integration enabled on the JS
// source binds the workspace, so no siteId is passed here. Skipped under
// Playwright or when the key is absent (local / preview-without-key) so it
// no-ops cleanly. AnalyticsBrowser.load returns a buffered client — store it
// directly (do not await) so calls queue until the SDK is ready.
const CUSTOMERIO_CDP_KEY = process.env.VITE_CUSTOMERIO_CDP_KEY || undefined;

let customerIoAnalytics: AnalyticsBrowser | undefined;
if (!IS_PATIENT_PORTAL && !window.Playwright && CUSTOMERIO_CDP_KEY) {
  customerIoAnalytics = AnalyticsBrowser.load(
    { writeKey: CUSTOMERIO_CDP_KEY },
    { integrations: { "Customer.io In-App Plugin": {} } },
  );
}

const useRudderStackAnalytics = () => {
  return rudderAnalytics;
};

/** The same client, for code outside a component. */
const rudderStackAnalytics = () => rudderAnalytics;

const useCustomerIoAnalytics = () => {
  return customerIoAnalytics;
};

/**
 * Identify the current user with our analytics providers, keyed on the
 * canonical spree_users.id. Centralizing identification here keeps every call
 * site on a single stable identifier (avoiding the duplicate/unstable
 * identities that arise when some call sites identify by email and others by
 * id) and keeps RudderStack and Customer.io in lockstep on the same id — the
 * same Segment `identify(userId, traits)` shape feeds both.
 *
 * No trait allow-list / PHI scrub here: `traits` is the clinician user's own
 * account data (name/email from `UserInfo`) — clinic/user-level metadata, never
 * patient or clinical data. `UserInfo` carries no order/patient fields, so
 * patient PHI cannot structurally reach this payload. A key-level allow-list
 * wouldn't catch the only residual (a patient name typed into a clinician's own
 * free-text name field) and adds nothing over the fixed {name, email} shape.
 */
const identifyUser = (
  spreeUserId: string | number,
  traits?: Pick<IdentifyTraits, "name" | "email">,
) => {
  const id = String(spreeUserId);
  rudderAnalytics?.identify(id, traits);
  customerIoAnalytics?.identify(id, traits);
};

export {
  useRudderStackAnalytics,
  rudderStackAnalytics,
  useCustomerIoAnalytics,
  identifyUser,
};
